import { useMemo } from 'react'
import { type ChakraProps, type SystemStyleObject, useTheme } from '@chakra-ui/react'

/**
 * Class names on the footer parts. They are the contract between the footer
 * components and `useFooterThemeStyles`, which targets them from the footer
 * root so a single store-theme token can restyle a nested part.
 */
export const footerClassNames = {
  column: 'footer-column',
  sectionTitle: 'footer-column__section-title',
  items: 'footer-column__items',
  itemLabel: 'footer-column__item-label',
  itemText: 'footer-column__item-text',
  itemLink: 'footer-column__item-link'
} as const

/**
 * Footer colours, read from `colors.component.Footer` in the store theme —
 * the same `colors.component.*` place the top-nav colours live in, so every
 * colour is edited on the Theme → Colors tab.
 */
export interface FooterColorTokens {
  /** The footer surface itself. */
  root?: { background?: string; color?: string }
  /** Column headings ("Kundservice", "Följ oss", …). */
  sectionTitle?: { color?: string }
  /** The bold label rendered above an item's link. */
  itemLabel?: { color?: string }
  /** Item links and plain item text inside a column. */
  link?: { color?: string }
}

/**
 * Non-colour typography knobs for the column headings, read from
 * `components.CustomFooter.sectionTitle` — component props stay in
 * `components.*`, colours stay in `colors.component.*`.
 */
export interface FooterSectionTitleStyles {
  fontSize?: SystemStyleObject['fontSize']
  fontWeight?: SystemStyleObject['fontWeight']
  letterSpacing?: SystemStyleObject['letterSpacing']
  textTransform?: SystemStyleObject['textTransform']
}

/**
 * The resolved `CustomFooter` style config: plain Chakra props for the footer
 * container, plus the nested `sectionTitle` part.
 */
export type FooterStyleConfig = ChakraProps & {
  sectionTitle?: FooterSectionTitleStyles
}

const definedEntries = (obj?: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(obj ?? {}).filter(([, value]) => value !== undefined && value !== '')
  )

const nested = (className: string, styles: Record<string, unknown>) =>
  Object.keys(styles).length ? { [`.${className}`]: styles } : {}

/**
 * Merges the `CustomFooter` style config with the `colors.component.Footer`
 * tokens into a single `sx` object for the footer container.
 *
 * Colours win over the style config, so the legacy `components.CustomFooter`
 * `bg` / `textColor` keep working for stores that never set the new tokens.
 * Nested parts are styled through descendant selectors (rather than CSS custom
 * properties) so Chakra still resolves colour tokens such as `text.light`, and
 * so an empty token in the theme is simply skipped instead of overriding the
 * component default with an empty value.
 */
export const useFooterThemeStyles = (styleConfig: FooterStyleConfig): SystemStyleObject => {
  const theme = useTheme()

  return useMemo(() => {
    const colors = (theme?.colors?.component?.Footer ?? {}) as FooterColorTokens
    const { sectionTitle: sectionTitleProps, ...containerStyles } = styleConfig

    const root = definedEntries(colors.root)
    const sectionTitle = {
      ...definedEntries(colors.sectionTitle),
      ...definedEntries(sectionTitleProps as Record<string, unknown>)
    }
    const itemLabel = definedEntries(colors.itemLabel)
    const link = definedEntries(colors.link)

    return {
      ...containerStyles,
      ...(root.background ? { bg: root.background } : {}),
      ...(root.color ? { color: root.color } : {}),
      ...nested(footerClassNames.sectionTitle, sectionTitle),
      ...nested(footerClassNames.itemLabel, itemLabel),
      ...(Object.keys(link).length
        ? {
            [`.${footerClassNames.itemLink}, .${footerClassNames.itemText}`]: link
          }
        : {})
    } as SystemStyleObject
  }, [styleConfig, theme])
}
